import {
  Box,
  Button,
  Checkbox,
  FormControlLabel,
  TextField,
  Typography,
  Alert,
  CircularProgress,
  Paper,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
} from "@mui/material";
import { useState, useEffect, useCallback, useMemo } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import DOMPurify from "dompurify";
import DrWeedyLogo from "../assets/DrWeedyLogo";
import { apiService, TermsResponse } from "../services/api";
import PlanCheckout, { PlanType } from "../components/PlanCheckout";
import SalesClosedNotice from "../components/SalesClosedNotice";
import { useSalesOpen } from "../hooks/useSalesOpen";
import TermsActions from "../components/TermsActions";
import { useReadToEnd } from "../hooks/useReadToEnd";

const BR_STATES = [
  "AC", "AL", "AP", "AM", "BA", "CE", "DF", "ES", "GO", "MA",
  "MT", "MS", "MG", "PA", "PB", "PR", "PE", "PI", "RJ", "RN",
  "RS", "RO", "RR", "SC", "SP", "SE", "TO",
];

const inputSx = {
  "& .MuiOutlinedInput-root": {
    borderRadius: "16px",
    backgroundColor: "rgba(255,255,255,0.05)",
    color: "#ffffff",
    fontSize: "16px",
    "& fieldset": { borderColor: "rgba(255,255,255,0.15)" },
    "&:hover fieldset": { borderColor: "rgba(226, 114, 91, 0.4)" },
    "&.Mui-focused fieldset": { borderColor: "#E2725B", borderWidth: "2px" },
  },
  "& .MuiInputLabel-root": { color: "rgba(255,255,255,0.6)", fontSize: "15px" },
  "& .MuiInputLabel-root.Mui-focused": { color: "#E2725B" },
  "& .MuiFormHelperText-root": { color: "rgba(255,255,255,0.85)" },
};

const formatCpf = (v: string) => {
  const d = v.replace(/\D/g, "").slice(0, 11);
  if (d.length <= 3) return d;
  if (d.length <= 6) return `${d.slice(0, 3)}.${d.slice(3)}`;
  if (d.length <= 9) return `${d.slice(0, 3)}.${d.slice(3, 6)}.${d.slice(6)}`;
  return `${d.slice(0, 3)}.${d.slice(3, 6)}.${d.slice(6, 9)}-${d.slice(9)}`;
};

const formatPhone = (v: string) => {
  const d = v.replace(/\D/g, "").slice(0, 11);
  if (d.length <= 2) return d ? `(${d}` : "";
  if (d.length <= 6) return `(${d.slice(0, 2)}) ${d.slice(2)}`;
  return `(${d.slice(0, 2)}) ${d.slice(2, 7)}-${d.slice(7)}`;
};

const formatCep = (v: string) => v.replace(/\D/g, "").slice(0, 8).replace(/(\d{5})(\d)/, "$1-$2");

const parsePlanParam = (value: string | null): PlanType => {
  if (value === "monthly" || value === "annual" || value === "lifetime") return value;
  return "annual"; // default alinhado ao card "Recomendado" da landing
};

export default function SignupPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  // Plano/trial vindos da landing (?plan=annual&trial=1) → pré-seleção no checkout.
  const initialPlan = useMemo(() => parsePlanParam(searchParams.get("plan")), [searchParams]);
  const initialTrial = useMemo(() => {
    const t = searchParams.get("trial");
    return t === "1" || t === "true";
  }, [searchParams]);

  const salesClosed = useSalesOpen() === false;
  const [step, setStep] = useState<"form" | "verify" | "terms" | "plan">("form");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const [name, setName] = useState("");
  const [cpf, setCpf] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [phone, setPhone] = useState("");
  const [councilType, setCouncilType] = useState("CRM");
  const [councilNumber, setCouncilNumber] = useState("");
  const [speciality, setSpeciality] = useState("");
  const [billingZipCode, setBillingZipCode] = useState("");
  const [billingLine1, setBillingLine1] = useState("");
  const [billingCity, setBillingCity] = useState("");
  const [billingState, setBillingState] = useState("");
  const [documentCommitment, setDocumentCommitment] = useState(false);
  const [verificationCode, setVerificationCode] = useState("");
  const [loadingCep, setLoadingCep] = useState(false);

  // Passo de termos (aceite ANTES do pagamento).
  const [terms, setTerms] = useState<TermsResponse | null>(null);
  const [loadingTerms, setLoadingTerms] = useState(false);
  const [accepting, setAccepting] = useState(false);
  const { ref, atEnd, idle, onScroll, jumpToEnd, wake } = useReadToEnd(terms);

  const fetchAddressByCep = useCallback(async (cep: string) => {
    const digits = cep.replace(/\D/g, "");
    if (digits.length !== 8) return;
    setLoadingCep(true);
    try {
      const res = await fetch(`https://viacep.com.br/ws/${digits}/json/`);
      const data = await res.json();
      if (data.erro) return;
      if (data.localidade) setBillingCity(data.localidade);
      if (data.uf) setBillingState(data.uf);
      if (data.logradouro) setBillingLine1(data.logradouro);
    } catch {
    } finally {
      setLoadingCep(false);
    }
  }, []);

  const handleCepBlur = () => {
    if (billingZipCode.replace(/\D/g, "").length === 8) {
      fetchAddressByCep(billingZipCode);
    }
  };

  const handleSubmitForm = async (e: React.FormEvent) => {
    e.preventDefault();
    setError("");
    if (password !== confirmPassword) {
      setError("As senhas não coincidem.");
      return;
    }
    if (!documentCommitment) {
      setError("Você precisa aceitar o compromisso de envio de documentos.");
      return;
    }
    setLoading(true);
    try {
      await apiService.doctorSignup({
        name,
        cpf: cpf.replace(/\D/g, ""),
        email,
        password,
        phone: phone.replace(/\D/g, ""),
        councilNumber,
        councilType,
        speciality,
        billingAddress: {
          line_1: billingLine1,
          zip_code: billingZipCode.replace(/\D/g, ""),
          city: billingCity,
          state: billingState,
        },
        documentCommitmentAccepted: true,
      });
      setStep("verify");
    } catch (err) {
      setError(err instanceof Error ? err.message : "Erro ao criar conta");
    } finally {
      setLoading(false);
    }
  };

  const handleVerifyCode = async (e: React.FormEvent) => {
    e.preventDefault();
    setError("");
    if (verificationCode.length !== 8) {
      setError("O código deve ter 8 dígitos.");
      return;
    }
    setLoading(true);
    try {
      const res = await apiService.verifyEmailSignup(email, verificationCode);
      if (res.verified && res.token) {
        // O cookie httpOnly já chega no Set-Cookie da resposta; só avança o step.
        setStep("terms");
      } else {
        navigate("/login", { replace: true });
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : "Código inválido ou expirado");
    } finally {
      setLoading(false);
    }
  };

  // Carrega os termos ao entrar no passo "terms".
  useEffect(() => {
    if (step !== "terms" || terms) return;
    setLoadingTerms(true);
    apiService
      .getTerms()
      .then(setTerms)
      .catch(() => setError("Erro ao carregar os termos de uso"))
      .finally(() => setLoadingTerms(false));
  }, [step, terms]);

  const sanitizedContent = useMemo(
    () => (terms ? DOMPurify.sanitize(terms.content, { WHOLE_DOCUMENT: true }) : ""),
    [terms],
  );

  const handleAcceptTerms = async () => {
    if (!terms) return;
    setAccepting(true);
    setError("");
    try {
      await apiService.acceptTerms(terms.id);
      setStep("plan");
    } catch {
      setError("Erro ao aceitar os termos. Tente novamente.");
    } finally {
      setAccepting(false);
    }
  };

  // Pós-compra: deixa os guards decidirem. O full reload re-hidrata o
  // AuthContext (assinatura + termos reais) — sem ele o guard mandaria a /subscribe.
  const handleActivated = (result: { status: string }) => {
    if (result.status === "active" || result.status === "pending") {
      navigate("/chat", { replace: true });
      window.location.reload();
    } else {
      setError("Não foi possível ativar a assinatura. Tente novamente.");
    }
  };

  // Venda fechada: o cadastro só existe para comprar, então nem abre.
  if (salesClosed) {
    return (
      <div className="flex min-h-screen bg-gradient-to-br from-petrolio-dark via-[#0a1a21] to-[#04090a] items-center justify-center px-6 py-12">
        <div className="w-full">
          <div className="flex items-center gap-3 mb-6 justify-center">
            <DrWeedyLogo width={40} height={40} />
            <Typography variant="h6" sx={{ color: "#fff", fontWeight: 700, fontFamily: "Oxanium, sans-serif" }}>
              Dr. Weedy
            </Typography>
          </div>
          <SalesClosedNotice />
          <div className="text-center mt-6">
            <Link to="/login" style={{ color: "#E2725B", fontWeight: 600 }}>
              Já é assinante? Entrar
            </Link>
          </div>
        </div>
      </div>
    );
  }

  if (step === "plan") {
    return (
      <div className="flex min-h-screen bg-gradient-to-br from-petrolio-dark via-[#0a1a21] to-[#04090a] relative overflow-hidden">
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top_right,_var(--tw-gradient-stops))] from-saluton/15 via-transparent to-transparent"></div>
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_bottom_left,_var(--tw-gradient-stops))] from-petrolio/10 via-transparent to-transparent"></div>
        <div className="w-full flex flex-col justify-center px-6 py-12 relative z-10">
          <div className="max-w-lg w-full mx-auto">
            <div className="flex items-center gap-3 mb-6 justify-center">
              <DrWeedyLogo width={40} height={40} />
              <Typography variant="h6" sx={{ color: "#fff", fontWeight: 700, fontFamily: "Oxanium, sans-serif" }}>
                Dr. Weedy
              </Typography>
            </div>

            <div className="text-center mb-6">
              <Typography variant="h5" sx={{ color: "#fff", fontWeight: 700, mb: 1, fontFamily: "Oxanium, sans-serif" }}>
                Escolha seu plano
              </Typography>
              <Typography sx={{ color: "rgba(255,255,255,0.7)", fontSize: 14 }}>
                Cadastro concluído. Selecione seu plano e ative o acesso ao seu assistente.
              </Typography>
            </div>

            <PlanCheckout
              availablePlans={["monthly", "annual", "lifetime"]}
              initialPlan={initialPlan}
              initialTrial={initialTrial}
              prefilledBilling={{
                line_1: billingLine1,
                zip_code: billingZipCode.replace(/\D/g, ""),
                city: billingCity,
                state: billingState,
              }}
              onActivated={handleActivated}
            />
          </div>
        </div>
      </div>
    );
  }

  if (step === "terms") {
    return (
      <div className="flex min-h-screen bg-gradient-to-br from-petrolio-dark via-[#0a1a21] to-[#04090a] relative overflow-hidden">
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top_right,_var(--tw-gradient-stops))] from-saluton/15 via-transparent to-transparent"></div>
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_bottom_left,_var(--tw-gradient-stops))] from-petrolio/10 via-transparent to-transparent"></div>
        <div className="w-full flex flex-col relative z-10">
          <div className="flex items-center gap-3 px-8 py-4 justify-center">
            <DrWeedyLogo width={40} height={40} />
            <Typography variant="h6" sx={{ color: "#fff", fontWeight: 700, fontFamily: "Oxanium, sans-serif" }}>
              Dr. Weedy
            </Typography>
          </div>
          <div className="flex-1 flex items-center justify-center px-6 py-4">
            <div className="max-w-3xl w-full">
              <div className="text-center mb-6">
                <Typography variant="h4" sx={{ color: "#fff", fontWeight: 700, mb: 1, letterSpacing: "-0.03em", fontFamily: "Oxanium, sans-serif" }}>
                  Termos de Uso
                </Typography>
                <Typography sx={{ color: "rgba(255,255,255,0.6)", fontSize: 15 }}>
                  Leia e aceite os termos para concluir sua assinatura
                </Typography>
              </div>

              <Paper
                elevation={8}
                onPointerDown={wake}
                sx={{
                  background: "linear-gradient(135deg, rgba(26, 49, 60, 0.8) 0%, rgba(4, 9, 10, 0.9) 100%)",
                  backdropFilter: "blur(20px)",
                  border: "2px solid rgba(226, 114, 91, 0.2)",
                  borderRadius: "24px",
                  padding: "32px",
                  boxShadow: "0 20px 60px rgba(0,0,0,0.5), 0 0 0 1px rgba(226, 114, 91, 0.1)",
                }}
              >
                <Box className="space-y-4">
                  {error && (
                    <Alert severity="error" sx={{ borderRadius: "16px", background: "rgba(225, 100, 100, 0.15)", border: "1px solid rgba(225, 100, 100, 0.3)", color: "#e16464", fontWeight: 500 }}>
                      {error}
                    </Alert>
                  )}

                  {loadingTerms ? (
                    <div className="flex justify-center py-20">
                      <CircularProgress sx={{ color: "#E2725B" }} />
                    </div>
                  ) : terms ? (
                    <>
                      {terms.version && (
                        <div className="inline-block px-3 py-1 bg-saluton/10 rounded-full border border-saluton/30 text-saluton-light text-xs font-medium">
                          Versão {terms.version}
                        </div>
                      )}

                      <div
                        ref={ref}
                        onScroll={onScroll}
                        className="overflow-y-auto pr-2"
                        style={{ maxHeight: "55vh", scrollbarWidth: "thin", scrollbarColor: "rgba(226, 114, 91, 0.3) transparent" }}
                      >
                        <div
                          dangerouslySetInnerHTML={{ __html: sanitizedContent }}
                          style={{ color: "rgba(255,255,255,0.85)", fontSize: "14px", lineHeight: "1.7" }}
                        />
                      </div>

                      <TermsActions
                        atEnd={atEnd}
                        idle={idle}
                        accepting={accepting}
                        acceptLabel="Li e aceito os Termos de Uso"
                        onAccept={handleAcceptTerms}
                        onJump={jumpToEnd}
                      />
                    </>
                  ) : null}
                </Box>
              </Paper>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (step === "verify") {
    return (
      <div className="flex min-h-screen bg-gradient-to-br from-petrolio-dark via-[#0a1a21] to-[#04090a] relative overflow-hidden">
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top_right,_var(--tw-gradient-stops))] from-saluton/15 via-transparent to-transparent"></div>
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_bottom_left,_var(--tw-gradient-stops))] from-petrolio/10 via-transparent to-transparent"></div>
        <div className="w-full flex flex-col justify-center px-12 py-16 relative z-10">
          <div className="max-w-md mx-auto w-full">
            <div className="text-center mb-8">
              <div className="flex justify-center mb-4"><DrWeedyLogo width={64} height={64} /></div>
              <Typography variant="h5" sx={{ color: "#ffffff", fontWeight: 600, marginBottom: 1 }}>
                Verifique seu email
              </Typography>
              <Typography sx={{ color: "rgba(255,255,255,0.7)", fontSize: "0.95rem" }}>
                Enviamos um código de 8 dígitos para {email}. Digite-o abaixo:
              </Typography>
            </div>
            <Paper
              elevation={0}
              sx={{
                background: "rgba(255,255,255,0.03)",
                borderRadius: "24px",
                padding: 3,
                border: "1px solid rgba(255,255,255,0.08)",
              }}
            >
              <form onSubmit={handleVerifyCode}>
                {error && (
                  <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError("")}>
                    {error}
                  </Alert>
                )}
                <TextField
                  fullWidth
                  label="Código"
                  value={verificationCode}
                  onChange={(e) => setVerificationCode(e.target.value.replace(/\D/g, "").slice(0, 8))}
                  placeholder="00000000"
                  sx={inputSx}
                  inputProps={{ maxLength: 8, inputMode: "numeric" }}
                  autoFocus
                />
                <Button
                  type="submit"
                  fullWidth
                  variant="contained"
                  disabled={loading || verificationCode.length !== 8}
                  sx={{
                    mt: 2,
                    py: 1.5,
                    borderRadius: "16px",
                    textTransform: "none",
                    fontSize: "16px",
                    fontWeight: 600,
                    background: "linear-gradient(135deg, #E2725B 0%, #CC3B2A 100%)",
                  }}
                >
                  {loading ? <CircularProgress size={24} color="inherit" /> : "Confirmar"}
                </Button>
              </form>
            </Paper>
            <Typography sx={{ mt: 2, textAlign: "center", color: "rgba(255,255,255,0.5)", fontSize: "0.9rem" }}>
              <Link to="/login" style={{ color: "#E2725B" }}>Voltar ao login</Link>
            </Typography>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="flex min-h-screen bg-gradient-to-br from-petrolio-dark via-[#0a1a21] to-[#04090a] relative overflow-hidden">
      <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top_right,_var(--tw-gradient-stops))] from-saluton/15 via-transparent to-transparent"></div>
      <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_bottom_left,_var(--tw-gradient-stops))] from-petrolio/10 via-transparent to-transparent"></div>
      <div className="w-full flex flex-col justify-center px-12 py-16 relative z-10 overflow-y-auto">
        <div className="max-w-lg mx-auto w-full">
          <div className="text-center mb-8">
            <div className="flex justify-center mb-4"><DrWeedyLogo width={64} height={64} /></div>
            <Typography variant="h5" sx={{ color: "#ffffff", fontWeight: 600, marginBottom: 1 }}>
              Criar conta
            </Typography>
            <Typography sx={{ color: "rgba(255,255,255,0.7)", fontSize: "0.95rem" }}>
              Crie sua conta para assinar o Dr. Weedy
            </Typography>
          </div>
          <Paper
            elevation={0}
            sx={{
              background: "rgba(255,255,255,0.03)",
              borderRadius: "24px",
              padding: 3,
              border: "1px solid rgba(255,255,255,0.08)",
            }}
          >
            <form onSubmit={handleSubmitForm}>
              {error && (
                <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError("")}>
                  {error}
                </Alert>
              )}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <TextField
                  fullWidth
                  label="Nome completo"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  required
                  sx={inputSx}
                />
                <TextField
                  fullWidth
                  label="CPF"
                  value={cpf}
                  onChange={(e) => setCpf(formatCpf(e.target.value))}
                  required
                  sx={inputSx}
                />
                <TextField
                  fullWidth
                  label="Email"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  sx={inputSx}
                />
                <TextField
                  fullWidth
                  label="Telefone"
                  value={phone}
                  onChange={(e) => setPhone(formatPhone(e.target.value))}
                  required
                  sx={inputSx}
                />
                <TextField
                  fullWidth
                  label="Senha"
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  helperText="Mín. 8 caracteres, 1 maiúscula e 1 minúscula"
                  sx={inputSx}
                />
                <TextField
                  fullWidth
                  label="Repetir senha"
                  type="password"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  required
                  error={confirmPassword.length > 0 && password !== confirmPassword}
                  helperText={confirmPassword.length > 0 && password !== confirmPassword ? "As senhas não coincidem" : ""}
                  sx={inputSx}
                />
                <FormControl fullWidth sx={inputSx}>
                  <InputLabel>Tipo de Profissional</InputLabel>
                  <Select
                    value={councilType}
                    onChange={(e) => setCouncilType(e.target.value)}
                    label="Tipo de Profissional"
                    required
                    sx={{
                      borderRadius: "16px",
                      color: "#ffffff",
                      "& .MuiSelect-icon": { color: "rgba(255,255,255,0.5)" },
                    }}
                  >
                    <MenuItem value="CRM">Médico(a)</MenuItem>
                    <MenuItem value="CRO">Dentista</MenuItem>
                  </Select>
                </FormControl>
                <TextField
                  fullWidth
                  label={councilType === "CRO" ? "CRO" : "CRM"}
                  value={councilNumber}
                  onChange={(e) => setCouncilNumber(e.target.value)}
                  required
                  placeholder={councilType === "CRO" ? "Ex: CRO/SP 123456" : "Ex: CRM/SP 123456"}
                  sx={inputSx}
                />
                <TextField
                  fullWidth
                  label="Especialidade"
                  value={speciality}
                  onChange={(e) => setSpeciality(e.target.value)}
                  required
                  sx={inputSx}
                />
                <TextField
                  fullWidth
                  label="CEP"
                  value={billingZipCode}
                  onChange={(e) => setBillingZipCode(formatCep(e.target.value))}
                  onBlur={handleCepBlur}
                  required
                  disabled={loadingCep}
                  sx={inputSx}
                  inputProps={{ maxLength: 9 }}
                />
                <TextField
                  fullWidth
                  label="Endereço"
                  value={billingLine1}
                  onChange={(e) => setBillingLine1(e.target.value)}
                  required
                  sx={inputSx}
                />
                <TextField
                  fullWidth
                  label="Cidade"
                  value={billingCity}
                  onChange={(e) => setBillingCity(e.target.value)}
                  required
                  sx={inputSx}
                />
                <FormControl fullWidth sx={inputSx}>
                  <InputLabel>Estado</InputLabel>
                  <Select
                    value={billingState}
                    onChange={(e) => setBillingState(e.target.value)}
                    label="Estado"
                    required
                    sx={{
                      borderRadius: "16px",
                      color: "#ffffff",
                      "& .MuiSelect-icon": { color: "rgba(255,255,255,0.5)" },
                    }}
                  >
                    {BR_STATES.map((s) => (
                      <MenuItem key={s} value={s}>{s}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </div>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={documentCommitment}
                    onChange={(e) => setDocumentCommitment(e.target.checked)}
                    sx={{ color: "rgba(255,255,255,0.5)", "&.Mui-checked": { color: "#E2725B" } }}
                  />
                }
                label={
                  <Typography sx={{ color: "rgba(255,255,255,0.85)", fontSize: "0.9rem" }}>
                    Comprometo-me a enviar os documentos comprovativos de médico/dentista quando solicitado pela plataforma. No caso do não envio, o acesso poderá ser suspenso ou revogado.
                  </Typography>
                }
                sx={{ mt: 2, display: "block" }}
              />
              <Button
                type="submit"
                fullWidth
                variant="contained"
                data-test='signup'
                disabled={loading || !documentCommitment || password !== confirmPassword}
                sx={{
                  mt: 2,
                  py: 1.5,
                  borderRadius: "16px",
                  textTransform: "none",
                  fontSize: "16px",
                  fontWeight: 600,
                  background: "linear-gradient(135deg, #E2725B 0%, #CC3B2A 100%)",
                }}
              >
                {loading ? <CircularProgress size={24} color="inherit" /> : "Criar conta e continuar"}
              </Button>
            </form>
          </Paper>
          <Typography sx={{ mt: 2, textAlign: "center", color: "rgba(255,255,255,0.5)", fontSize: "0.9rem" }}>
            Já tem conta? <Link to="/login" style={{ color: "#E2725B" }}>Entrar</Link>
          </Typography>
        </div>
      </div>
    </div>
  );
}
